import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, catchError, EMPTY, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { AddValueDriverDto, UpdateValueDriverDto, ValueDriver } from '../../models/value-driver';
import { ValueDriverService } from './value-driver.service';

/**
 * A stateful Facade that holds and manages
 * the global filter state. It uses the API services to fetch/mutate
 * data and updates its internal state.
 */
@Injectable({
  providedIn: 'root',
})
export class ValueDriverFacade {
  // --- State ---
  private readonly valueDrivers$ = new BehaviorSubject<ValueDriver[]>([]);

  // --- Injected Services ---
  private readonly valueDriverService = inject(ValueDriverService);

  // --- Public Observable (Getter) ---

  getValueDrivers(): Observable<ValueDriver[]> {
    return this.valueDrivers$.asObservable();
  }

  // --- State-Modifying Methods ---

  setValueDrivers(): void {
    this.valueDriverService.getValueDrivers().subscribe({
      next: (valueDrivers) => {
        const sortedDrivers = [...valueDrivers].sort((a, b) => a.sort - b.sort);
        const normalizedDrivers = sortedDrivers.map((driver, index) => ({
          ...driver,
          sort: index, // Assign 0-based index
        }));

        this.valueDrivers$.next(normalizedDrivers);
      },
      error: (err) => console.error('Failed to fetch value drivers:', err),
    });
  }

  addValueDriver(valueDriverData: AddValueDriverDto): void {
    this.valueDriverService
      .createValueDriver(valueDriverData)
      .pipe(
        switchMap((newValueDriver) => {
          const currentState = this.valueDrivers$.getValue();

          const updatedState = [...currentState];
          updatedState.splice(valueDriverData.sort, 0, newValueDriver);

          const normalizedState = updatedState.map((driver, index) => ({
            ...driver,
            sort: index,
          }));

          this.valueDrivers$.next(normalizedState);

          const valueDriverIds = normalizedState.map((driver) => driver.id);

          return this.valueDriverService.updateValueDriversOrder(valueDriverIds).pipe(
            catchError((sortErr) => {
              console.error('Failed to update value driver order after add:', sortErr);
              this.valueDrivers$.next(currentState);
              return throwError(() => sortErr);
            }),
          );
        }),
        catchError((addErr) => {
          console.error('Failed to create value driver:', addErr);
          return of(null);
        }),
      )
      .subscribe();
  }

  editValueDriver(valueDriverId: number, updateDto: UpdateValueDriverDto): void {
    // 1. Get original state for rollback
    const originalDrivers = this.valueDrivers$.getValue();
    const fromIndex = originalDrivers.findIndex((d) => d.id === valueDriverId); // <-- Change
    if (fromIndex === -1) return;

    // 2. Get 0-based target index
    const targetIndex = updateDto.sort ?? fromIndex;
    const needsReorder = targetIndex !== fromIndex;

    let optimisticallyUpdatedDrivers: ValueDriver[];

    // 3. Create optimistic state
    if (needsReorder) {
      // --- REORDER + EDIT ---
      optimisticallyUpdatedDrivers = [...originalDrivers];
      const [itemToMove] = optimisticallyUpdatedDrivers.splice(fromIndex, 1);
      const newInsertIndex = fromIndex < targetIndex ? targetIndex - 1 : targetIndex;
      optimisticallyUpdatedDrivers.splice(newInsertIndex, 0, itemToMove);

      optimisticallyUpdatedDrivers = optimisticallyUpdatedDrivers.map((driver, index) => {
        const updatedDriver = { ...driver, sort: index };
        if (index === newInsertIndex) {
          return {
            ...updatedDriver,
            name: updateDto.name ?? updatedDriver.name,
          };
        }
        return updatedDriver;
      });
    } else {
      // --- SIMPLE EDIT (no reorder) ---
      optimisticallyUpdatedDrivers = originalDrivers.map((driver) => {
        if (driver.id === valueDriverId) {
          const { sort, ...dtoData } = updateDto;
          return { ...driver, ...dtoData };
        }
        return driver;
      });
    }

    // 4. Optimistic update
    this.valueDrivers$.next(optimisticallyUpdatedDrivers);

    // 5. Prepare DTO for the API call
    const dataUpdateDto: UpdateValueDriverDto = {
      name: updateDto.name,
      sort: targetIndex,
    };

    // 6. Start the API Call Chain
    this.valueDriverService
      .editValueDriver(valueDriverId, dataUpdateDto)
      .pipe(
        switchMap((updatedDriverFromServer) => {
          // 7. Re-sync state
          const finalizedDrivers = optimisticallyUpdatedDrivers.map((driver) =>
            driver.id === valueDriverId ? { ...driver, ...updatedDriverFromServer, sort: driver.sort } : driver,
          );
          this.valueDrivers$.next(finalizedDrivers);

          // 8. If reorder is needed, chain the sorting call
          if (needsReorder) {
            const valueDriverIds = finalizedDrivers.map((d) => d.id);
            return this.valueDriverService.updateValueDriversOrder(valueDriverIds).pipe(
              catchError((sortErr) => {
                console.error('Failed to update value driver order after edit, rolling back...', sortErr);
                this.valueDrivers$.next(originalDrivers);
                return of(EMPTY);
              }),
            );
          } else {
            return of(updatedDriverFromServer);
          }
        }),
        catchError((editErr) => {
          console.error(`Failed to edit value driver ${valueDriverId}, rolling back...`, editErr); // <-- Change
          this.valueDrivers$.next(originalDrivers);
          return of(EMPTY);
        }),
      )
      .subscribe();
  }

  updateValueDriversOrder(valueDrivers: ValueDriver[]): void {
    const oldState = this.valueDrivers$.getValue();
    this.valueDrivers$.next(valueDrivers);
    const valueDriverIds = valueDrivers.map((driver) => driver.id);

    this.valueDriverService
      .updateValueDriversOrder(valueDriverIds)
      .pipe(
        catchError((err) => {
          this.valueDrivers$.next(oldState);
          console.error('Failed to save new value driver order:', err);
          return of(EMPTY);
        }),
      )
      .subscribe();
  }

  deleteValueDriver(valueDriverId: number): Observable<void> {
    return this.valueDriverService.deleteValueDriver(valueDriverId).pipe(
      tap(() => {
        const vd = this.valueDrivers$.getValue().filter((vd) => vd.id !== valueDriverId);
        this.valueDrivers$.next(vd);
      }),
      catchError((error) => {
        return throwError(() => error);
      }),
    );
  }
}
